import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff, Lock, Mail, User, Building2, Briefcase, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import temwiseLogo from "@/assets/temwise-logo.png";
import loginCityscape from "@/assets/login-cityscape.jpg";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type PortalType = "buyer" | "supplier" | null;

const SignIn = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [firmName, setFirmName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState<PortalType>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { signIn, signUp, user } = useAuth();

  // Get redirect URL from query params
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get("redirect");

  // Only redirect after a SUCCESSFUL sign-in action, not on page load
  // This prevents stale sessions from auto-redirecting users
  const handlePostSignInRedirect = async (userId: string) => {
    // If there's a redirect URL, go there
    if (redirectTo) {
      navigate(redirectTo);
      return;
    }
    
    // Redirect based on user's actual role
    await checkUserRoleAndRedirect(userId);
  };

  const checkUserRoleAndRedirect = async (userId: string) => {
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    if (roles && roles.length > 0) {
      const hasSupplier = roles.some(r => r.role === "supplier");
      const hasBuyer = roles.some(r => r.role === "buyer");

      // Strictly enforce role-based routing
      if (selectedPortal === "buyer") {
        // User selected In-House portal
        if (hasBuyer) {
          navigate("/dashboard");
        } else {
          // User doesn't have buyer role but selected buyer portal
          // Stay on sign-in and show error
          return;
        }
      } else if (selectedPortal === "supplier") {
        // User selected Supplier portal
        if (hasSupplier) {
          navigate("/supplier/dashboard");
        } else {
          // User doesn't have supplier role but selected supplier portal
          // Stay on sign-in and show error
          return;
        }
      }
    } else {
      // No roles yet - this is a new user, redirect based on portal selection
      if (selectedPortal === "supplier") {
        navigate("/supplier/dashboard");
      } else if (selectedPortal === "buyer") {
        navigate("/dashboard");
      }
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    const { error } = await signIn(email, password);
    
    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to sign in",
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
      return;
    }

    // Check role and add missing role if needed (users can have both roles)
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    if (currentUser) {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", currentUser.id);

      const hasSupplier = roles?.some(r => r.role === "supplier") || false;
      const hasBuyer = roles?.some(r => r.role === "buyer") || false;

      // Add the role if user doesn't have it yet (users can have multiple roles)
      if (selectedPortal === "buyer" && !hasBuyer) {
        await supabase.from("user_roles").insert({
          user_id: currentUser.id,
          role: "buyer"
        });
      } else if (selectedPortal === "supplier" && !hasSupplier) {
        await supabase.from("user_roles").insert({
          user_id: currentUser.id,
          role: "supplier"
        });
      }

      toast({
        title: "Success",
        description: "You have successfully signed in",
        duration: 3000,
      });

      // Handle redirect after successful sign-in
      await handlePostSignInRedirect(currentUser.id);
    }
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Please enter your name",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (selectedPortal === "supplier" && !firmName.trim()) {
      toast({
        title: "Error",
        description: "Please enter your firm name",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    
    const { error } = await signUp(email, password, name);
    
    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create account",
        variant: "destructive",
        duration: 3000,
      });
      setLoading(false);
    } else {
      // After signup, get the user and assign role
      const { data: { user: newUser } } = await supabase.auth.getUser();
      
      if (newUser) {
        // Assign role
        await supabase.from("user_roles").insert({
          user_id: newUser.id,
          role: selectedPortal || "buyer"
        });

        // If supplier, create supplier profile
        if (selectedPortal === "supplier") {
          await supabase.from("supplier_profiles").insert({
            user_id: newUser.id,
            firm_name: firmName
          });
        }
      }

      toast({
        title: "Success",
        description: "Account created! You can now sign in.",
        duration: 3000,
      });
      // Reset form
      setEmail("");
      setPassword("");
      setName("");
      setFirmName("");
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter your email",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setResetLoading(true);
    
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send reset email",
        variant: "destructive",
        duration: 3000,
      });
    } else {
      toast({
        title: "Success",
        description: "Password reset email sent! Check your inbox.",
        duration: 5000,
      });
      setResetDialogOpen(false);
      setResetEmail("");
    }
    
    setResetLoading(false);
  };

  // Portal selection screen
  if (!selectedPortal) {
    return (
      <div className="flex h-screen signin-page">
        {/* Left side - Portal Selection */}
        <div className="w-1/2 flex flex-col justify-center items-center px-6 py-6 bg-[hsl(235,88%,11%)] text-white">
          <div className="w-full max-w-lg space-y-8">
            <div className="flex justify-center">
              <img 
                src={temwiseLogo} 
                alt="Temwise Logo"
                className="h-20 w-auto"
              />
            </div>
            
            <div className="text-center space-y-2">
              <h1 className="text-2xl font-bold">Welcome to Temwise</h1>
              <p className="text-gray-300">Choose your portal to continue</p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {/* In-House Team Portal */}
              <Card 
                className="cursor-pointer border-2 border-transparent hover:border-white/50 transition-all bg-white/10 backdrop-blur"
                onClick={() => setSelectedPortal("buyer")}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-white/20">
                      <Building2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">In-House Legal Team</CardTitle>
                      <CardDescription className="text-gray-300">
                        For corporate legal departments
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>• Create and manage instructions or RFPs</li>
                    <li>• Run reverse auctions</li>
                    <li>• Compare law firm proposals</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Law Firm Portal */}
              <Card 
                className="cursor-pointer border-2 border-transparent hover:border-white/50 transition-all bg-white/10 backdrop-blur"
                onClick={() => setSelectedPortal("supplier")}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-white/20">
                      <Briefcase className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-white text-lg">Law Firm</CardTitle>
                      <CardDescription className="text-gray-300">
                        For external legal service providers
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="text-sm text-gray-300 space-y-1">
                    <li>• Respond to instructions or RFPs</li>
                    <li>• Submit competitive bids</li>
                    <li>• Participate in live auctions</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        
        {/* Right side - Graphic */}
        <div className="w-1/2 flex items-center justify-center p-0 bg-white overflow-hidden">
          <img 
            src={loginCityscape}
            alt="City skyline"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen signin-page">
      {/* Left side - Auth forms */}
      <div className="w-1/2 flex flex-col justify-center items-center px-6 py-6 bg-[hsl(235,88%,11%)] text-white">
        <div className="w-full max-w-md space-y-4">
          <div className="flex justify-center">
            <img 
              src={temwiseLogo} 
              alt="Temwise Logo"
              className="h-20 w-auto"
            />
          </div>

          {/* Back button and portal indicator */}
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/10"
              onClick={() => setSelectedPortal(null)}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2 text-sm text-gray-300">
              {selectedPortal === "buyer" ? (
                <>
                  <Building2 className="h-4 w-4" />
                  In-House Team
                </>
              ) : (
                <>
                  <Briefcase className="h-4 w-4" />
                  Law Firm
                </>
              )}
            </div>
          </div>
          
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-gray-900">
              <TabsTrigger value="signin">Sign In</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <div className="space-y-4">
                <h1 className="text-xl font-bold text-center">Sign In</h1>
                <p className="text-center text-gray-400 text-xs">Enter your credentials to access your account</p>
                
                <form onSubmit={handleSignIn} className="space-y-3">
                  {/* Email field */}
                  <div>
                    <label htmlFor="signin-email" className="block text-xs font-medium mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Mail className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="Enter your email"
                        className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Password field */}
                  <div>
                    <label htmlFor="signin-password" className="block text-xs font-medium mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        id="signin-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-white text-[#042cc4] hover:bg-gray-100 h-9"
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>
                
                <div className="text-center">
                  <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                    <DialogTrigger asChild>
                      <button className="text-xs text-gray-400 hover:text-white transition-colors">
                        Forgot Password?
                      </button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogDescription>
                          Enter your email address and we'll send you a link to reset your password.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handlePasswordReset} className="space-y-4">
                        <div>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              type="email"
                              placeholder="Enter your email"
                              className="pl-10"
                              value={resetEmail}
                              onChange={(e) => setResetEmail(e.target.value)}
                              required
                            />
                          </div>
                        </div>
                        <Button 
                          type="submit" 
                          className="w-full"
                          disabled={resetLoading}
                        >
                          {resetLoading ? "Sending..." : "Send Reset Link"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="signup">
              <div className="space-y-4">
                <h1 className="text-xl font-bold text-center">Create Account</h1>
                <p className="text-center text-gray-400 text-xs">Sign up to get started</p>
                
                <form onSubmit={handleSignUp} className="space-y-3">
                  {/* Name field */}
                  <div>
                    <label htmlFor="signup-name" className="block text-xs font-medium mb-1">
                      Full Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <User className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="Enter your name"
                        className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Firm Name field - only for suppliers */}
                  {selectedPortal === "supplier" && (
                    <div>
                      <label htmlFor="signup-firm" className="block text-xs font-medium mb-1">
                        Law Firm Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                          <Briefcase className="h-4 w-4 text-gray-400" />
                        </div>
                        <Input
                          id="signup-firm"
                          type="text"
                          placeholder="Enter your firm name"
                          className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                          value={firmName}
                          onChange={(e) => setFirmName(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Email field */}
                  <div>
                    <label htmlFor="signup-email" className="block text-xs font-medium mb-1">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Mail className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="Enter your email"
                        className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  {/* Password field */}
                  <div>
                    <label htmlFor="signup-password" className="block text-xs font-medium mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Lock className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        id="signup-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password (min 6 characters)"
                        className="pl-10 bg-gray-900 border-gray-700 text-white h-9"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-gray-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-white text-[#042cc4] hover:bg-gray-100 h-9"
                    disabled={loading}
                  >
                    {loading ? "Creating account..." : "Sign Up"}
                  </Button>
                </form>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* Right side - Graphic */}
      <div className="w-1/2 flex items-center justify-center p-0 bg-white overflow-hidden">
        <img 
          src={loginCityscape}
          alt="City skyline"
          className="w-full h-full object-cover"
        />
      </div>
    </div>
  );
};

export default SignIn;
